import { Component, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AsyncDropdown } from 'src/app/models/async-dropdown';
import { SortObject } from 'src/app/models/sortObject';
import { ToastService } from 'src/app/services/toast.service';
import { checkForValidationErrors } from 'src/app/utils/checkForValidationErrors';
import { BudgetService } from '../../service/budget.service';
import { ProjectsService } from '../service/projects.service';
import { UnlinkProjectComponent } from '../unlink-project/unlink-project.component';

@Component({
  selector: 'apc-order-migrate-project',
  templateUrl: './migrate-project.component.html',
  styleUrls: ['./migrate-project.component.scss', '../link-existing-project/link-existing-project.component.scss'],
})
export class MigrateProjectComponent {
  @Input() budgetId: string;
  @Input() projectId: string;
  @Input() budgetName: string;
  @Input() projectName: string;
  @Input() budget: any;
  @Input() budgets: any;
  @Input() orderName: string;
  @Output() backEvent = new EventEmitter<boolean>();
  @Output() operationSuccess = new EventEmitter<any>();
  @Output() hideModalEvent = new EventEmitter<any>();
  @Output() showModalEvent = new EventEmitter<any>();
  @Output() closeEventEmitter = new EventEmitter<any>();
  
  unlinkText: string;
  showProductsDecision: boolean = false;

  public unlinkModalRef: any;

  public form: UntypedFormGroup = null;
  // order search
  orderSearch = new Subject<any>();
  queryOrderString: string = '';
  searchOrderValue: string = '';
  orderResult: any = '';
  isLoadingOrder: boolean = false;
  orderSelected: boolean = false;
  selectedOrderIdentifier: string;
  selectedOrderName: string;

  // order search end

  public orderBudgets: AsyncDropdown[] = [];
  public orderBudgetsLoading: boolean = false;
  public budgetPlaceholder: string = 'Please select a destination order';
  public disableBudgetSelect: boolean = true;

  constructor(
    private projectService: ProjectsService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private budgetService: BudgetService
  ) {
    this.orderSearch.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((input) => {
      this.searchOrder(input.value);
    });
    this.initSingleForm();
  }

  initSingleForm() {
    this.form = new UntypedFormGroup({
      destinationBudgetIdentifier: new UntypedFormControl(null, [Validators.required]),
    });
  }
  /* order search methods */
  async searchOrder(value: string) {
    this.searchOrderValue = value;
    this.queryOrderString = this.searchOrderValue;
    this.isLoadingOrder = true;
    this.orderResult = [];
    this.orderSelected = false;
    this.budgetPlaceholder = 'Please select a destination order';
    this.form.reset();
    this.disableBudgetSelect = true;

    if (this.queryOrderString.length > 0) {
      this.projectService.getOrderSearch(this.queryOrderString).then(
        (res) => {
          this.orderResult = res;
          this.isLoadingOrder = false;
        },
        (reason) => {
          this.isLoadingOrder = false;
        }
      );
    } else {
      this.orderResult = [];
      this.isLoadingOrder = false;
    }
  }

  closeOrderSearch() {
    this.orderResult = [];
    this.searchOrderValue = '';
    this.queryOrderString = '';
    this.selectedOrderIdentifier = undefined;
    this.isLoadingOrder = false;
    this.disableBudgetSelect = true;
    this.budgetPlaceholder = 'Please select a destination order';
    this.form.reset();
  }

  selectOrder(order) {
    this.searchOrderValue = this.constructOrderDisplayName(order);
    this.selectedOrderIdentifier = order.identifier;
    this.selectedOrderName = order.orderName;
    this.orderSelected = true;
    this.budgetPlaceholder = 'Loading Budgets...';
    this.fetchBudgetsForOrder();
  }

  constructOrderDisplayName(order) {
    return order.orderName + ' (' + order.orderCode + ')';
  }
  /* order search methods end*/

  async fetchBudgetsForOrder() {
    this.orderBudgets = [];
    this.orderBudgetsLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.projectService.getBudgetSearch(this.selectedOrderIdentifier, sort).then((res) => {
      res.forEach((item: any) => {
        let days = (Number(item.hoursAssigned) / 8).toFixed(2);
        item.hoursAssigned = Number(item.hoursAssigned).toFixed(2) + 'h (' + days + 'd)';
      });
      this.orderBudgets = res;
      this.orderBudgets.sort(function (a: any, b: any) {
        return a.disabled - b.disabled;
      });

      if (this.orderBudgets.length > 0) {
        this.disableBudgetSelect = false;
        this.budgetPlaceholder = '';
      } else {
        this.disableBudgetSelect = true;
        this.budgetPlaceholder = 'There are no budgets available';
      }
      this.orderBudgetsLoading = false;
    });
  }

  async migrate() {
    if (this.form.valid) {
      this.hideModalEvent.emit(true);
      this.openUnlinkModal();
      this.unlinkModalRef.componentInstance.unlinkText =
        'Are you sure you want to migrate project <b>' +
        this.projectName +
        '</b> between budgets? <br/><br/>' +
        'Source Budget: <b>' + 
        this.budgetName +
        '</b> (Order ' + this.orderName + ') <br/>' +
        'Destination Budget: <b>' +
        this.form.value.destinationBudgetIdentifier.budgetName +
        '</b> (Order ' + this.selectedOrderName + ')';
      this.unlinkModalRef.result.then(
        (res) => {},
        (res) => {
          // if user pressed yes, unlink the project
          if (res.answer) {
            this.projectService
              .migrateProject(this.selectedOrderIdentifier, this.budgetId, this.projectId, {
                destinationBudgetIdentifier: this.form.value.destinationBudgetIdentifier.budgetIdentifier,
              })
              .then((migrateRes) => {
                this.toastService.showSuccess('Project migrated!');
                this.operationSuccess.emit(migrateRes);

                /* projcet migrated, start deletion budget flow */
                this.unlinkText =
                'Budget <strong>' + this.budget.name + '</strong> is now unused, do you want to delete it?';
                this.openUnlinkModal();
                if (this.budget.budgetProducts.length > 0 && this.budgets.length > 1) {
                  // text 6 + text 4
                  this.unlinkText =
                    'Budget <strong>' +
                    this.budget.name +
                    '</strong> is now unused, do you want to delete it? <br/> <br/> Budget has assigned products (' +
                    this.budget.budgetProducts.length +
                    '), select what you want to do with them.';
                  this.showProductsDecision = true;
                  this.unlinkModalRef.componentInstance.showProductsDecision = this.showProductsDecision;
                  this.orderBudgets.forEach((el : any) => {
                    el.identifier = el.budgetIdentifier;
                    el.name = el.budgetName;
                    el.description = null;
                  })
                  this.unlinkModalRef.componentInstance.budgets = this.budgets.filter(
                    (el) => el.identifier !== this.budget.identifier
                  );
                }
                this.unlinkModalRef.componentInstance.unlinkText = this.unlinkText;
                this.unlinkModalRef.result.then(
                  (res) => {},
                  (res: any) => {
                    if (res.answer) {
                      var budgetToMigrate = res.migrateId;
                      var selectedMigrationBudgetName = res.selectedMigrationBudgetName;
                      this.budgetService.deleteBudgetAndMigrateHours(this.budgetId, res.migrateId).then((res) => {
                        if (budgetToMigrate) {
                          this.toastService.showSuccess(
                            'Budget deleted successfully! Assigned products from ' +
                              this.budget.name +
                              ' got migrated to the budget ' +
                              selectedMigrationBudgetName
                          );

                        } else {
                          this.toastService.showSuccess('Budget deleted successfully!');
                        }
                        //this does not emit and becuase of that budget is not deleted from ux :(
                        this.emitClose({ delete: true, migrate: true, budget: res, budgetId: this.budgetId });
                        // doing reload until we fix this
                        window.location.reload();
                      });
                    } else {
                      this.emitClose({ delete: false, migrate: true, budget: migrateRes, budgetId: this.budgetId });
                    }
                  }
                );
                /* budget deletion flow end */

              })
              .catch((error) => {
                this.showModalEvent.emit(true);
                checkForValidationErrors(error, this.form);
              });
          } else {
            this.showModalEvent.emit(true);
          }
        }
      );
    } else {
      this.toastService.showError('Destination budget is required!');
      this.form.markAsPristine();
      this.form.markAllAsTouched();
    }
  }

  openUnlinkModal() {
    this.unlinkModalRef = this.modalService.open(UnlinkProjectComponent, {
      ariaLabelledBy: 'modal-basic',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'budget-filler-modal',
    });
    this.unlinkModalRef.componentInstance.modalRef = this.unlinkModalRef;
  }
  
  emitClose(payload) {
    console.log(payload)
    this.closeEventEmitter.emit(payload);
  }

  cancel() {
    this.backEvent.emit();
  }
}
